import { Store } from '@reduxjs/toolkit';
import { reportErrorToSentry } from '../setup/sentry';

import { displayMessagesFetched, localeChanged } from './langSlice';
import { DEFAULT_LOCALE, getSupportedLocale as defaultGetSupportedLocale } from './lang';
import { trace } from '../setup/trace';
import { config } from '../../config';

const sendError = (exception: Error) => {
    reportErrorToSentry(exception, {
        tags: {
            module: config.sentryModuleName,
        },
    });
};

// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
const normalizeDynamicImport = (imported: any): any => imported.default ?? imported;

export const importDisplayMessages = (locale: string) =>
    import(`../../features/translations/${locale}.json`).then(normalizeDynamicImport).catch((error: Error) => {
        if (error === undefined) {
            error = new Error('undefined error while loading translations');
        }
        sendError(error);
        throw error;
    });

export const configureFetchDisplayMessages =
    (store: Store, fetchDisplayMessages = importDisplayMessages, getSupportedLocale = defaultGetSupportedLocale) =>
    async (locale: string) => {
        if (!locale) {
            const errorMessage = 'No "locale" supplied when fetching display messages!';
            console.warn(errorMessage);
            return Promise.reject(new Error(errorMessage));
        }

        const supportedLocale = getSupportedLocale(locale);

        try {
            /* eslint-disable @typescript-eslint/no-unsafe-assignment */
            const displayMessages = await fetchDisplayMessages(supportedLocale);
            trace(`Display messages fetched for "${supportedLocale}"`);
            store.dispatch(displayMessagesFetched({ locale: supportedLocale, displayMessages }));
            /* eslint-enable @typescript-eslint/no-unsafe-assignment */
        } catch (error) {
            console.error(`Display messages for "${supportedLocale}" could not be fetched.`, error);
            sendError(error as Error);
            store.dispatch(localeChanged(DEFAULT_LOCALE));
        }
    };
